import type { DraggableMetaType } from "../../../../../../../../api/types/svTypes.ts";

export interface FieldDestination {
  id: string;
  title: string;
  canAdd: (field: DraggableMetaType) => boolean;
  add: (field: DraggableMetaType) => void;
}

export interface FieldDestinationSnapshot {
  destination?: FieldDestination;
  destinations: FieldDestination[];
}

export interface FieldDestinationStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => FieldDestinationSnapshot;
  register: (destination: FieldDestination) => void;
  unregister: (id: string) => void;
  select: (id?: string) => void;
  addField: (field: DraggableMetaType, destinationId?: string) => void;
}

export const createFieldDestinationStore = (): FieldDestinationStore => {
  const destinations = new Map<string, FieldDestination>();
  const listeners = new Set<() => void>();
  let selected: FieldDestination | undefined;
  let snapshot: FieldDestinationSnapshot = { destinations: [] };
  const notify = () => {
    snapshot = { destination: selected, destinations: [...destinations.values()] };
    listeners.forEach((listener) => listener());
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
    register: (destination) => {
      destinations.set(destination.id, destination);
      if (selected?.id === destination.id) {
        selected = destination;
      }
      notify();
    },
    unregister: (id) => {
      if (!destinations.delete(id)) {
        return;
      }
      if (selected?.id === id) {
        selected = undefined;
      }
      notify();
    },
    select: (id) => {
      const destination = id === undefined ? undefined : destinations.get(id);
      if (destination !== selected) {
        selected = destination;
        notify();
      }
    },
    addField: (field, destinationId) => {
      const destination = destinationId === undefined ? selected : destinations.get(destinationId);
      if (destination?.canAdd(field)) {
        destination.add(field);
      }
    },
  };
};

export const getEligibleFieldDestinations = (
  { destination, destinations }: FieldDestinationSnapshot,
  field: DraggableMetaType
): FieldDestination[] => (destination ? [destination] : destinations).filter((item) => item.canAdd(field));
